<template>
  <div
    v-if="isVisible"
    class="exam-status-modal-overlay"
  >
    <div class="exam-status-modal">
      <!-- Loading State -->
      <div
        v-if="status === 'loading'"
        class="exam-status-modal__loading"
      >
        <loading />
        <h3 class="exam-status-modal__title">{{ loadingTitle }}</h3>
        <p class="exam-status-modal__message">{{ loadingMessage }}</p>
      </div>

      <!-- Success State -->
      <div
        v-else-if="status === 'success'"
        class="exam-status-modal__success"
      >
        <div class="exam-status-modal__icon">
          <svg-loader src="/static/images/check.svg" />
        </div>

        <div class="exam-status-modal__content">
          <h3 class="exam-status-modal__title">{{ successTitle }}</h3>

          <p
            v-if="message"
            class="exam-status-modal__message"
          >
            {{ message }}
          </p>

          <p class="exam-status-modal__info">
            {{ successInfo }}
          </p>
        </div>

        <div class="exam-status-modal__timer">
          <div class="timer-bar">
            <div
              class="timer-fill"
              :style="{ width: timerProgress + '%' }"
            ></div>
          </div>
          <span class="timer-text">{{ remainingSeconds }}s</span>
        </div>

        <button
          class="exam-status-modal__close-btn exam-status-modal__close-btn--success"
          @click="closeModal"
        >
          Fechar
        </button>
      </div>

      <!-- Error State -->
      <div
        v-else-if="status === 'error'"
        class="exam-status-modal__error"
      >
        <div class="exam-status-modal__icon">
          <svg-loader src="/static/images/icon-error.svg" />
        </div>

        <div class="exam-status-modal__content">
          <h3 class="exam-status-modal__title">{{ errorTitle }}</h3>

          <p class="exam-status-modal__message">
            {{ message }}
          </p>
        </div>

        <button
          class="exam-status-modal__close-btn exam-status-modal__close-btn--error"
          @click="closeModal"
        >
          Fechar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from './Loading.vue'

export default {
  name: 'ExamStatusModal',
  components: {
    Loading,
  },
  props: {
    loadingTitle: {
      type: String,
      default: 'Incluindo Exame',
    },
    loadingMessage: {
      type: String,
      default: 'Aguarde enquanto processamos sua solicitação...',
    },
    successTitle: {
      type: String,
      default: 'Exame Adicionado com Sucesso!',
    },
    successInfo: {
      type: String,
      default:
        'O exame pode levar alguns segundos para aparecer na listagem devido ao processamento.',
    },
    errorTitle: {
      type: String,
      default: 'Erro ao Incluir Exame',
    },
    duration: {
      type: Number,
      default: 7000,
    },
  },
  data() {
    return {
      isVisible: false,
      status: 'loading', // 'loading', 'success', 'error'
      message: '',
      remainingSeconds: 7,
      timerProgress: 100,
      timer: null,
      progressTimer: null,
    }
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    showLoading(message = '') {
      this.status = 'loading'
      this.message = message
      this.isVisible = true
      this.clearTimers()
    },

    showSuccess(message = '') {
      this.status = 'success'
      this.message = message
      this.remainingSeconds = Math.ceil(this.duration / 1000)
      this.timerProgress = 100
      this.startTimer()
    },

    showError(message = '') {
      this.status = 'error'
      this.message = message
      this.clearTimers()
    },

    closeModal() {
      this.clearTimers()
      this.isVisible = false
      this.$emit('closed', this.status)
    },

    startTimer() {
      // Timer para countdown dos segundos
      this.timer = setInterval(() => {
        this.remainingSeconds--
        if (this.remainingSeconds <= 0) {
          this.closeModal()
        }
      }, 1000)

      // Timer para barra de progresso (mais suave)
      const progressStep = 100 / (this.duration / 100) // atualiza a cada 100ms
      this.progressTimer = setInterval(() => {
        this.timerProgress -= progressStep
        if (this.timerProgress <= 0) {
          this.timerProgress = 0
        }
      }, 100)
    },

    clearTimers() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../static/stylesheets/variables';

.exam-status-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.exam-status-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-width: 480px;
  width: 90%;
  padding: 32px;
  text-align: center;
  animation: modalSlideIn 0.3s ease-out;

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;

    .loading_container {
      margin-bottom: 20px;
    }
  }

  &__icon {
    margin-bottom: 20px;

    svg {
      width: 64px;
      height: 64px;

      // Success color
      .exam-status-modal__success & {
        color: $ocean-green;
      }

      // Error color
      .exam-status-modal__error & {
        color: $amaranth;
      }
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 16px;
    margin-top: 0;

    // Loading state
    .exam-status-modal__loading & {
      color: $brand-primary;
    }

    // Success state
    .exam-status-modal__success & {
      color: $ocean-green;
    }

    // Error state
    .exam-status-modal__error & {
      color: $amaranth;
    }
  }

  &__message {
    font-size: 16px;
    color: $tundora;
    margin-bottom: 12px;
    font-weight: 500;
  }

  &__info {
    font-size: 14px;
    color: $label-color;
    margin-bottom: 24px;
    line-height: 1.4;
  }

  &__timer {
    margin-bottom: 24px;

    .timer-bar {
      background-color: #e9ecef;
      border-radius: 10px;
      height: 8px;
      overflow: hidden;
      margin-bottom: 8px;

      .timer-fill {
        background: linear-gradient(90deg, $ocean-green, $puerto-rico);
        height: 100%;
        transition: width 0.1s linear;
        border-radius: 10px;
      }
    }

    .timer-text {
      font-size: 12px;
      color: $label-color;
      font-weight: 500;
    }
  }

  &__close-btn {
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: white;

    &--success {
      background-color: $brand-primary;

      &:hover {
        background-color: $brand-primary-highlight;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(127, 26, 84, 0.25);
      }
    }

    &--error {
      background-color: $amaranth;

      &:hover {
        background-color: darken($amaranth, 10%);
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(226, 36, 90, 0.25);
      }
    }
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
